// tcms-frontend/src/pages/AdminReportPrimaryStudentsCentre.js
import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Card, Form } from "react-bootstrap";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import { BASE_URL, security_key } from "../global";

function AdminReportPrimaryStudentsCentre() {
    const location = useLocation();
    const navigate = useNavigate();

    // Prefer router state; fallback to sessionStorage (survives refresh)
    const state =
        location.state ||
        (() => {
            try {
                return JSON.parse(sessionStorage.getItem("reportCentreState") || "{}");
            } catch {
                return {};
            }
        })();

    const centKey = state.centKey;
    const centName = state.centName || "Centre";

    // Persist state once we have it (first entry)
    useEffect(() => {
        if (location.state) {
            sessionStorage.setItem(
                "reportCentreState",
                JSON.stringify(location.state)
            );
        } else if (!centKey) {
            // no state and no fallback — go back to overview
            navigate("/admin/AdminReportPrimaryStudents");
        }
    }, [location.state, centKey, navigate]);

    // default to current month as YYYY-MM
    const currentYM = useMemo(() => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    }, []);

    const [ym, setYm] = useState(currentYM);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        sessionStorage.setItem(
            "previousPagePathname",
            location.pathname + location.search
        );
    }, [location.pathname, location.search]);

    const fetchData = async () => {
        if (!centKey) return;
        setLoading(true);
        try {
            const [y, m] = ym.split("-").map((v) => parseInt(v, 10));
            const res = await axios.post(`${BASE_URL}API/api_index.php`, {
                security_key,
                mode: "getSessionsPerStudentByMonth",
                fk_cent_key: centKey,
                year: y,
                month: m,
            });

            if (res.data?.success && Array.isArray(res.data.data)) {
                const mapped = res.data.data.map((item, idx) => ({
                    id: idx + 1,
                    student_name: `${item.st_first_name ?? ""} ${
                        item.st_surname ?? ""
                    }`.trim(),
                    level_name: item.level_name ?? "",
                    session_count: Number(item.session_count ?? item.num_sessions ?? 0),
                }));
                setRows(mapped);
            } else {
                setRows([]);
            }
        } catch (e) {
            console.error("Fetch monthly session counts failed:", e);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(); // eslint-disable-next-line
    }, [ym, centKey]);

    const columns = [
        {
            field: "student_name",
            headerName: "Student",
            flex: 1,
            headerClassName: "super-app-theme--header",
        },
        {
            field: "level_name",
            headerName: "Year Level",
            flex: 1,
            headerClassName: "super-app-theme--header",
        },
        {
            field: "session_count",
            headerName: "Sessions (Selected Month)",
            flex: 1,
            headerClassName: "super-app-theme--header",
        },
    ];

    return (
        <div>
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="page-title">
                        {centName} — Monthly Sessions by Student
                    </h5>
                    <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
                        <NavLink to="/calendar">Home</NavLink>
                        <NavLink to="/reports">Reports</NavLink>
                        <NavLink to="/admin/AdminReportPrimaryStudents">
                            Primary Students
                        </NavLink>
                        <Typography color="text.primary">Centre</Typography>
                    </Breadcrumbs>
                </div>
                <div>
                    <button
                        className="btn btn-outline-primary"
                        onClick={() => navigate(-1)}
                    >
                        ← Back
                    </button>
                </div>
            </div>

            <Card className="card my-card mb-3">
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={4} sm={6} xs={12}>
                            <Form.Label>Select Month</Form.Label>
                            <Form.Control
                                type="month"
                                value={ym}
                                onChange={(e) => setYm(e.target.value)}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col xs="12">
                            <Box
                                sx={{
                                    width: "100%",
                                    overflowX: "auto",
                                    "& .super-app-theme--header": {
                                        backgroundColor: "#f4f4f4",
                                        whiteSpace: "normal",
                                    },
                                    "& .MuiDataGrid-cell": {
                                        whiteSpace: "normal",
                                        wordWrap: "break-word",
                                    },
                                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
                                        {
                                            outline: "none",
                                        },
                                }}
                            >
                                <DataGrid
                                    autoHeight
                                    rows={rows}
                                    columns={columns}
                                    loading={loading}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    rowHeight={56}
                                    slots={{ loadingOverlay: LinearProgress, toolbar: GridToolbar }}
                                    slotProps={{
                                        toolbar: {
                                            csvOptions: {
                                                fileName: `MonthlySessions_${centName}_${ym}`,
                                            },
                                            printOptions: {
                                                fileName: `MonthlySessions_${centName}_${ym}`,
                                                hideFooter: true,
                                                hideToolbar: true,
                                            },
                                            showQuickFilter: true,
                                        },
                                    }}
                                    disableRowSelectionOnClick
                                    showColumnVerticalBorder
                                    showCellVerticalBorder
                                />
                            </Box>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </div>
    );
}

export default AdminReportPrimaryStudentsCentre;
